import React, { useMemo, useEffect, useState, useRef } from 'react';
import classes from "./TotalBox.module.css"
import {
  useBooking,
  getPaidEquipmentPreisRowLabel,
  formatEquipmentPreisEuros,
  roundEuros,
} from "../../../store/bookingStore";
import { getSelectedPackages } from "../../../util/getSelectedPackages";
import { calculateCreativePackagesPrice } from "../../../util/calculateCreativePackagesPrice";
import { getWeekendSurchargeLineLabel } from "../../../util/getWeekendSurchargeLineLabel";
import { getDiscountLineLabel } from "../../../util/getDiscountLineLabel";
import { buildStudioCoverImageCandidates } from "../../../util/studioImageUtils";

const TotalBox = ({ 
    style, 
    data, 
    date, 
    startTime, 
    endTime,  
    basePrice, 
    totalPrice, 
    pricePerHour,
    durationInHours,
    setCurrentSlide,
    studioCreativesData
  }) => {
  const { bookingData } = useBooking();

  // Detect if this studio uses package-based pricing
  // IMPORTANT:
  // - Only treat the studio as "package pricing" when pricingMode is explicitly "PACKAGES"
  // - Studios with pricingMode === "HOURLY" must stay hourly even if packages exist
  const isPackagePricing = useMemo(
    () => data?.pricingMode === 'PACKAGES',
    [data?.pricingMode]
  );

  // Helper function to get base package price for selected people group
  // The package base-price display reads from the authoritative store values
  // (bookingData.packageBasePrice / bookingData.baseStudioPrice) written by
  // useBookingPricingSync / PaketAndGroupSize. The former local helper
  // getPackageBasePriceForGroup used an exact-key + prices[0] fallback that could
  // show a wrong tier on multi-package studios; it has been removed.

  // Get selected packages from studioCreativesData
  const selectedPackages = useMemo(() => {
    if (!studioCreativesData || !bookingData?.creativePackages || bookingData.creativePackages.length === 0) {
      return [];
    }
    return getSelectedPackages(studioCreativesData, bookingData.creativePackages);
  }, [studioCreativesData, bookingData?.creativePackages]);

  // Gesamtbetrag: immer direkt aus dem Booking-Store nehmen.
  // Dort wird der vollständige Gesamtpreis zentral berechnet
  // (Studio + Gebühren + Creative + Equipment).
  const displayPrice = useMemo(
    () => roundEuros(bookingData.totalPrice != null ? Number(bookingData.totalPrice) : 0),
    [bookingData.totalPrice]
  );
  const hasUnavailablePricing = Boolean(bookingData?.pricingUnavailable);

  const campaignDiscountAmount = useMemo(() => {
    if (!bookingData?.serverPriceSynced || !bookingData?.campaignApplied) return 0;
    const cents = Number(bookingData?.campaignDiscountAmountCents);
    if (!Number.isFinite(cents) || cents <= 0) return 0;
    return roundEuros(cents / 100);
  }, [
    bookingData?.serverPriceSynced,
    bookingData?.campaignApplied,
    bookingData?.campaignDiscountAmountCents,
  ]);

  const studioHourly = useMemo(() => {
    if (isPackagePricing) return null;
    if (durationInHours && bookingData.baseStudioPrice) {
      return Math.round((bookingData.baseStudioPrice / durationInHours) * 100) / 100;
    }
    return pricePerHour;
  }, [isPackagePricing, durationInHours, bookingData.baseStudioPrice, pricePerHour]);

  const studioCoverImageCandidates = useMemo(
    () => buildStudioCoverImageCandidates(data),
    [data?.id, data?.originalImages, data?.processedImages]
  );
  const [coverCandidateIndex, setCoverCandidateIndex] = useState(0);
  const coverImageErrorExhaustedRef = useRef(false);

  useEffect(() => {
    setCoverCandidateIndex(0);
    coverImageErrorExhaustedRef.current = false;
  }, [data?.id, studioCoverImageCandidates]);

  const studioImageSrc = studioCoverImageCandidates[coverCandidateIndex] ?? '';

  const handleStudioImageError = () => {
    if (coverImageErrorExhaustedRef.current) return;
    setCoverCandidateIndex((i) => {
      if (i + 1 >= studioCoverImageCandidates.length) {
        coverImageErrorExhaustedRef.current = true;
        return i;
      }
      return i + 1;
    });
  };

return (
    <div className={classes.totalBox} style={style}>
      <div className={classes.studioProfileContainer}>
        <div className={classes.studioProfile}>
          <div className={classes.studioProfileImage}>
            <img
              src={studioImageSrc}
              alt="Studio"
              onError={handleStudioImageError}
            />
          </div>
          <div className={classes.studioProfileText}>
            <div className={classes.studioProfileTextFirst}>
              <h3 className="mediumLarge">{data?.StudioName}</h3>
              <p className="slimRegularSmallText">{data?.city}, {data?.country}</p>
            </div>
            {data?.reviewCount &&
              <div className={classes.studioProfileTextSecond}>
                <p>4,5
                <svg xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
                  <g clipPath="url(#clip0_4470_15656)">
                    <path d="M6.42769 5.84833L6.74849 7.7286L4.3291 6.46321L6.28957 5.05078L6.38313 5.58991L6.42769 5.84833Z" fill="#1D1E1B"/>
                    <path d="M2.27553 5.58964L2.36909 5.05051L0.413086 3.15688L3.11763 2.76479L4.32956 0.332031L5.54148 2.76479L8.24603 3.15688L1.91017 7.72832L2.27553 5.58964Z" fill="#1D1E1B"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_4470_15656">
                      <rect width="7.83294" height="7.39629" fill="white" transform="translate(0.413086 0.332031)"/>
                    </clipPath>
                  </defs>
                </svg>
                </p>
                <span className="slimRegularSmallText">({data?.reviewCount})</span>
              </div>
            }
          </div>
        </div>
        <div className={classes.selectedPackagesContainer}>
          {selectedPackages?.map((pkg) => {
            return (
              <div className={classes.studioProfile} key={pkg.id}>
                <div className={classes.studioProfileImage}>
                  <img src={pkg?.processedPortfolioImages[0]} alt="Package" />
                </div>
                <div className={classes.selectedPackageText}>
                  <span className="mediumLarge">{pkg?.name}</span>
                  <ul className={classes.includedServicesList}>
                    {pkg?.includedServices?.map((service) => {
                      return (
                        <li style={{color: "var(--dark-grey-3)"}} className='slimRegularSmallText' key={service}>
                          {service}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={classes.horizontalLine}></div>
      <h2 className={`${classes.deineBuchung} titleSemibold3`}>Deine Buchung</h2>
      <div className={classes.bookingDetails}>
        <h3 className='semiboldSmall'>Datum und Zeit</h3>
        <div className={classes.bookingDetailsContainer}>
            <div className={classes.bookingDetailsText}>
                <p>{date}</p>
                <p>{startTime} - {endTime} Uhr</p>
            </div>
            <button onClick={() => setCurrentSlide(0)} className={classes.edit}>Bearbeiten</button>
        </div>
        {/* <div className={classes.bookingDetailsContainer} style={{alignItems: "flex-end"}}>
            <div className={classes.bookingDetailsText}>
                <h3 className='semiboldSmall'>Teilnehmer</h3>
                <p>{personCount} Personen</p>
            </div>
            <span className={classes.edit}>Bearbeiten</span>
        </div> */}
      </div>
      <div className={classes.horizontalLine}></div>
      <div className={`${classes.priceBreakdown} regularLarge`}>
      <h3 className='titleSemibold3'>Preis</h3>
        <div className={classes.priceItem}>
          <span className={classes.priceItemText}>
            {isPackagePricing
              ? "Paketpreis"
              : `${studioHourly} € x ${durationInHours} Stunden`}
          </span>
          <span style={{whiteSpace: 'nowrap'}}>
            {bookingData?.pricingUnavailable
              ? 'Nicht verfügbar'
              : isPackagePricing
                ? `${bookingData?.packageBasePrice || bookingData?.baseStudioPrice} €`
                : `${bookingData.baseStudioPrice || basePrice} €`}
          </span>
        </div>
        {hasUnavailablePricing && (
          <div className={classes.priceItem}>
            <span className={classes.priceItemText}>
              {bookingData.pricingUnavailableReason || 'Preis für diese Personengruppe nicht verfügbar.'}
            </span>
          </div>
        )}
        {!hasUnavailablePricing && isPackagePricing && Number(bookingData?.packagePeoplePrice) > 0 && (
          <div className={classes.priceItem}>
            <span className={classes.priceItemText}>Personengruppe</span>
            <span style={{whiteSpace: 'nowrap'}}>+{bookingData.packagePeoplePrice} €</span>
          </div>
        )}
        {/* Show discount if applied */}
        {!hasUnavailablePricing && bookingData?.appliedDiscount > 0 && (
          <div className={classes.priceItem}>
            <span className={classes.priceItemText}>
              {getDiscountLineLabel(bookingData)}
            </span>
            <span style={{whiteSpace: 'nowrap'}}>-{bookingData.appliedDiscount} €</span>
          </div>
        )}
        {!hasUnavailablePricing && campaignDiscountAmount > 0 && (
          <div className={classes.priceItem}>
            <span className={classes.priceItemText}>Kampagnenrabatt</span>
            <span style={{whiteSpace: 'nowrap'}}>-{formatEquipmentPreisEuros(campaignDiscountAmount)}</span>
          </div>
        )}
        {/* Show weekend surcharge if applied */}
        {!hasUnavailablePricing && bookingData?.appliedSurcharge > 0 && (
          <div className={classes.priceItem}>
            <span className={classes.priceItemText}>{getWeekendSurchargeLineLabel(bookingData)}</span>
            <span style={{whiteSpace: 'nowrap'}}>+{bookingData.appliedSurcharge} €</span>
          </div>
        )}
        {/* Show fees if applied */}
        {!hasUnavailablePricing && bookingData?.appliedFees && bookingData.appliedFees.length > 0 && (
          bookingData.appliedFees.map((fee) => (
            <div className={classes.priceItem} key={fee.id}>
              <span className={classes.priceItemText}>{fee.description || 'Gebühr'}</span>
              <span style={{whiteSpace: 'nowrap'}}>+{fee.amount} €</span>
            </div>
          ))
        )}
        {/* <div className={classes.priceItem}>
          <span>Fotograf/in: {photographerRate} € x {totalHours} Stunden</span>
          <span>{totalPhotographerCost} €</span>
        </div> */}
        {!hasUnavailablePricing && calculateCreativePackagesPrice(
          selectedPackages,
          isPackagePricing
            ? (Number(bookingData?.packageDurationHours) || durationInHours)
            : Math.max(durationInHours, Number(data?.minNumberOfHours) || 1)
        ).lines.map((line) => (
          <div className={classes.priceItem} key={line.id}>
            <span className={classes.priceItemText}>
              {line.name}:{' '}
              {line.isHourly
                ? `${line.unitPrice} € x ${line.hours} Stunden`
                : 'Pauschale'}
            </span>
            <span style={{whiteSpace: 'nowrap'}}>+{formatEquipmentPreisEuros(line.lineTotal)}</span>
          </div>
        ))}
        {!hasUnavailablePricing && bookingData?.equipment?.filter((item) => (Number(item.totalPrice) || 0) > 0).map((item, index) => (
          <div className={classes.priceItem} key={`eq-${item.name}-${index}`}>
            <span className={classes.priceItemText}>{getPaidEquipmentPreisRowLabel(item)}</span>
            <span style={{ whiteSpace: 'nowrap' }}>+{formatEquipmentPreisEuros(item.totalPrice)}</span>
          </div>
        ))}
        <div className={classes.horizontalLineThin}></div>
      </div>
      <div className={classes.totalAmount}>
      <h3 className='titleSemibold3'>Gesamtbetrag</h3>
      <span className='titleSemibold3' style={{whiteSpace: 'nowrap'}}>
        {hasUnavailablePricing ? 'Nicht verfügbar' : formatEquipmentPreisEuros(displayPrice)}
      </span>
      </div>
      <div className={classes.horizontalLine}></div>
    </div>
  );
};

export default TotalBox;
